import type { StatusCounts, Target } from './types';
import { EMPTY_COUNTS, type Source } from './types';

/**
 * What the dashboard shows, and why.
 *
 * Two independent reasons a machine can be hidden, and they compose in a
 * specific order that matters:
 *
 *   1. An OPERATOR decision — someone hid it, optionally with an expiry.
 *   2. A POLICY — Datto appliances with no client assigned, which on this
 *      tenant are decommissioned boxes still listed in the vendor portal.
 *
 * An explicit decision always beats the policy. `hidden === false` is therefore
 * meaningful and distinct from the field being absent: it means "someone looked
 * at this and said show it anyway", which is the only way to rescue a single
 * machine from a blanket rule without weakening the rule for everyone else.
 */

export type HideCategory = 'retired' | 'operator' | 'snoozed' | 'no-client';

/** Null when visible; otherwise why it is hidden. */
export function hideCategory(t: Target): HideCategory | null {
  /**
   * RETIRED WINS, and is not overridable by "show anyway".
   *
   * Retirement is a statement about whether we are watching at all, not a
   * display preference - so the way back is Restore, which clears it, rather
   * than un-hiding around it. Allowing `hidden === false` to override would
   * leave a machine on the board that nothing is collecting.
   */
  if (t.retiredAt) return 'retired';

  // Explicit "show this" overrides the policy below.
  if (t.hidden === false) return null;

  if (t.hidden === true) {
    const until = t.hiddenUntil?.toMillis?.();
    if (!until) return 'operator';
    // A snooze that has run out stops hiding on its own — the whole point of an
    // expiry is not having to remember to undo it.
    return until > Date.now() ? 'snoozed' : null;
  }

  if (t.source === 'datto' && !(t.client ?? '').trim()) return 'no-client';
  return null;
}

export function isHidden(t: Target): boolean {
  return hideCategory(t) !== null;
}

/** One line explaining an individual machine's hidden state. */
export function hiddenReason(t: Target): string {
  const cat = hideCategory(t);

  if (cat === 'retired') {
    const when = t.retiredAt?.toDate?.();
    const how = t.retiredReason === 'manual'
      ? `Retired${t.retiredBy ? ` by ${t.retiredBy}` : ''}`
      : 'Retired automatically after the vendor stopped reporting it';
    return `${how}${when ? ` on ${when.toLocaleDateString()}` : ''}`;
  }
  const who = t.hiddenBy ? ` by ${t.hiddenBy}` : '';
  const why = t.hiddenReason ? ` — ${t.hiddenReason}` : '';

  if (cat === 'snoozed') {
    const until = t.hiddenUntil?.toDate?.();
    return `Back on ${until ? until.toLocaleDateString() : 'an unknown date'}${who}${why}`;
  }
  if (cat === 'operator') return `Hidden indefinitely${who}${why}`;
  if (cat === 'no-client') return 'Datto appliance with no client assigned in the vendor portal';
  return '';
}

export const CATEGORY_LABEL: Record<HideCategory, string> = {
  retired: 'Retired',
  operator: 'Hidden indefinitely',
  snoozed: 'Snoozed',
  'no-client': 'No client assigned',
};

export const CATEGORY_NOTE: Record<HideCategory, string> = {
  retired:
    'No longer reported by the vendor, or retired by hand. Nothing is deleted - history, ' +
    'acknowledgements and notes are kept, and a machine returns to the board on its own if the ' +
    'vendor reports it again. Retiring is what separates "we decommissioned this" from "this ' +
    'vanished unexpectedly", which used to look identical.',
  operator: 'Someone chose to hide these and set no end date. They stay hidden until unhidden here.',
  snoozed: 'Temporarily hidden. These come back on their own — no action needed unless you want them sooner.',
  'no-client':
    'Hidden by a rule, not individually: Datto appliances with no client assigned are decommissioned ' +
    'boxes still listed in the vendor portal. Unhiding one here overrides the rule for that machine only. ' +
    'The durable fix is to assign it a client in Datto, or remove it there.',
};

export function partition(targets: Target[]): { visible: Target[]; hidden: Target[] } {
  const visible: Target[] = [];
  const hidden: Target[] = [];
  for (const t of targets) (isHidden(t) ? hidden : visible).push(t);
  return { visible, hidden };
}

/** Hidden machines grouped by why, in the order they deserve attention. */
export function groupHidden(hidden: Target[]): { category: HideCategory; items: Target[] }[] {
  // Retired last: it is the settled group, and the ones needing a decision
  // should not have to be scrolled past.
  const order: HideCategory[] = ['operator', 'snoozed', 'no-client', 'retired'];
  return order
    .map((category) => ({
      category,
      items: hidden.filter((t) => hideCategory(t) === category),
    }))
    .filter((g) => g.items.length > 0);
}

export function tally(items: readonly { status: Target['status'] }[]): StatusCounts {
  const out = { ...EMPTY_COUNTS, total: items.length };
  for (const i of items) out[i.status] += 1;
  return out;
}

/**
 * Counts are derived from the VISIBLE targets rather than read from
 * meta/summary, because the collectors count everything they sync — they have
 * no notion of a UI-side exclusion. Deriving here keeps the cards, the sidebar
 * badges and the table showing the same population.
 */
export function countsBySource(visible: Target[]): {
  overall: StatusCounts;
  bySource: Record<Source, StatusCounts>;
} {
  return {
    overall: tally(visible),
    bySource: {
      datto: tally(visible.filter((t) => t.source === 'datto')),
      sp: tally(visible.filter((t) => t.source === 'sp')),
      sc: tally(visible.filter((t) => t.source === 'sc')),
      msp: tally(visible.filter((t) => t.source === 'msp')),
      unitrends: tally(visible.filter((t) => t.source === 'unitrends')),
      email: tally(visible.filter((t) => t.source === 'email')),
    },
  };
}
